import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Minus, Plus, X } from "lucide-react";
import api, { apiErrorDetail } from "../lib/api";
import { useCart } from "../lib/cart";
import Reveal from "../components/Reveal";

export default function ProductDetail() {
  const { slug } = useParams();
  const { add } = useCart();
  const [product, setProduct] = useState(null);
  const [color, setColor] = useState(null);
  const [size, setSize] = useState(null);
  const [qty, setQty] = useState(1);
  const [activeImage, setActiveImage] = useState(null);
  const [zoom, setZoom] = useState(false);

  useEffect(() => { setActiveImage(null); setZoom(false); }, [slug]);
  useEffect(() => { setActiveImage(null); }, [color?.name]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setZoom(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    api.get(`/products/${slug}`)
      .then((r) => setProduct(r.data))
      .catch(() => toast.error("Product not found"));
  }, [slug]);

  if (!product) {
    return <main className="pt-40 pb-24 text-center font-mono text-[11px] tracking-widest text-mute">LOADING UNIT…</main>;
  }

  const colorImage = color?.image || "";
  const gallery = [
    ...(colorImage ? [colorImage] : []),
    ...(product.image && product.image !== colorImage ? [product.image] : []),
    ...(product.images || []).filter((u) => u && u !== colorImage && u !== product.image),
  ];
  const mainImage = activeImage || gallery[0] || "";

  const handleAdd = () => {
    if (!color) return toast.error("Select a colour");
    if (!size) return toast.error("Select a size");
    add({ productId: product._id, slug: product.slug, title: product.title, price: product.price, color: color.name, colorHex: color.hex, size, qty, image: colorImage || product.image });
  };

  return (
    <main className="pt-24 pb-24 px-5 md:px-10 max-w-[1600px] mx-auto">
      <div className="grid lg:grid-cols-2 gap-10">
        <Reveal className="relative">
          <div className="lg:sticky lg:top-24">
            <div className="clip-frame border border-white/10 overflow-hidden">
              <button
                type="button"
                data-testid="product-zoom-trigger"
                onClick={() => setZoom(true)}
                aria-label="Zoom photo"
                className="block w-full cursor-zoom-in"
              >
                <img src={mainImage} alt={product.title} data-testid="product-gallery-main" className="w-full h-[55vh] lg:h-[78vh] object-contain bg-[#0B0B0D]" />
              </button>
              {product.tag && (
                <span className="absolute top-5 left-5 font-mono text-[10px] tracking-[0.2em] bg-volt text-void px-2.5 py-1.5 font-bold">{product.tag}</span>
              )}
            </div>
            {gallery.length > 1 && (
              <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
                {gallery.map((img, i) => (
                  <button
                    key={img + i}
                    data-testid={`product-gallery-thumb-${i}`}
                    onClick={() => setActiveImage(img)}
                    aria-label={`View photo ${i + 1}`}
                    className={`shrink-0 border transition-colors duration-200 ${mainImage === img ? "border-acid" : "border-white/15 hover:border-white/40"}`}
                  >
                    <img src={img} alt={`${product.title} view ${i + 1}`} className="w-14 h-16 md:w-16 md:h-20 object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </Reveal>

        <Reveal delay={0.1} className="flex flex-col">
          <p className="font-mono text-[11px] tracking-[0.3em] text-volt">DROP 001 / {product.code?.toUpperCase()}</p>
          <h1 className="mt-4 font-display font-extrabold uppercase tracking-tighter text-3xl sm:text-4xl lg:text-5xl leading-[0.95]">
            {product.title}
          </h1>
          <div className="mt-5 flex items-baseline gap-4 flex-wrap">
            <p className="font-mono text-2xl font-bold text-acid">₹{product.price.toLocaleString("en-IN")}</p>
            {product.original_price > product.price && (
              <>
                <p className="font-mono text-sm text-mute line-through">₹{product.original_price.toLocaleString("en-IN")}</p>
                <span data-testid="product-detail-save-badge" className="font-mono text-[10px] tracking-[0.2em] bg-acid text-void px-2.5 py-1 font-bold">SAVE ₹{(product.original_price - product.price).toLocaleString("en-IN")}</span>
              </>
            )}
          </div>
          <p className="mt-6 text-dim text-sm leading-relaxed max-w-lg">{product.description}</p>

          <div className="mt-8 grid grid-cols-2 gap-px bg-white/10 border border-white/10 font-mono text-[10px] tracking-widest">
            <div className="bg-[#0E1015] px-4 py-3"><span className="text-mute">FABRIC —</span> <span className="text-ink">{product.gsm}</span></div>
            <div className="bg-[#0E1015] px-4 py-3"><span className="text-mute">FIT —</span> <span className="text-ink">{product.fit}</span></div>
          </div>

          {/* COLOUR */}
          <div className="mt-9">
            <p className="font-mono text-[11px] tracking-[0.25em] text-dim mb-4">COLOUR — <span className="text-ink">{color?.name?.toUpperCase()}</span></p>
            <div className="flex gap-3">
              {product.colors.map((c) => (
                <button
                  key={c.name}
                  data-testid={`product-color-select-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => setColor(c)}
                  aria-label={`Colour ${c.name}`}
                  className={`w-10 h-10 rounded-full border-2 transition-all duration-300 ${color?.name === c.name ? "border-acid scale-110" : "border-white/20 hover:border-white/50"}`}
                  style={{ background: c.hex }}
                />
              ))}
            </div>
          </div>

          {/* SIZE */}
          <div className="mt-9">
            <p className="font-mono text-[11px] tracking-[0.25em] text-dim mb-4">SIZE — <span className="text-ink">{size || "SELECT"}</span></p>
            <div className="flex flex-wrap gap-3">
              {product.sizes.map((s) => (
                <button
                  key={s}
                  data-testid={`product-size-select-${s.toLowerCase()}`}
                  onClick={() => setSize(s)}
                  className={`min-w-[56px] px-4 py-3 font-mono text-xs tracking-widest border transition-all duration-300 ${size === s ? "bg-ink text-void border-ink font-bold" : "border-white/20 text-dim hover:border-acid hover:text-acid"}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* QTY + ADD */}
          <div className="mt-10 flex flex-wrap items-stretch gap-4">
            <div className="flex items-center border border-white/20">
              <button data-testid="product-qty-minus" onClick={() => setQty(Math.max(1, qty - 1))} className="px-4 py-4 text-dim hover:text-white"><Minus size={13} /></button>
              <span data-testid="product-qty-value" className="font-mono text-sm w-10 text-center">{qty}</span>
              <button data-testid="product-qty-plus" onClick={() => setQty(Math.min(10, qty + 1))} className="px-4 py-4 text-dim hover:text-white"><Plus size={13} /></button>
            </div>
            <button
              data-testid="product-add-to-cart-btn"
              onClick={handleAdd}
              className="flex-1 min-w-[200px] bg-volt text-void font-display font-bold text-sm tracking-widest px-8 py-4 hover:bg-acid transition-colors duration-300"
            >
              ADD TO CART — ₹{(product.price * qty).toLocaleString("en-IN")}
            </button>
          </div>

          <p className="mt-4 font-mono text-[10px] tracking-widest text-mute">
            SKU: <span className="text-acid">{color?.sku}-{size || "?"}</span> · MADE TO ORDER · DISPATCH IN 24-48H
          </p>

          <div className="mt-10 border-t border-white/10">
            {[
              ["FABRIC & CARE", "Machine wash cold, inside out. Do not bleach. Dry in shade. Heavyweight cotton softens with every wash — never loses structure."],
              ["SHIPPING & COD", "Free shipping pan-India. Cash on Delivery available at serviceable pincodes (+₹49). Prepaid orders ship fastest via Razorpay."],
              ["MADE TO ORDER", "Every unit is manufactured on demand the moment you order — printed, quality-checked and dispatched with live tracking. No deadstock. No restocks."],
            ].map(([t, b]) => (
              <details key={t} className="border-b border-white/10 group">
                <summary className="flex justify-between items-center py-4 font-mono text-[11px] tracking-[0.25em] text-dim group-open:text-acid">
                  {t}<span className="text-volt">+</span>
                </summary>
                <p className="pb-5 text-sm text-dim leading-relaxed">{b}</p>
              </details>
            ))}
          </div>
        </Reveal>
      </div>

      {zoom && (
        <div data-testid="photo-zoom-overlay" className="fixed inset-0 z-[95] bg-[#060709]/[0.98] backdrop-blur-xl overflow-auto">
          <div className="min-h-full flex items-center justify-center px-4 py-16">
            <img
              src={mainImage}
              alt={product.title}
              data-testid="photo-zoom-image"
              onClick={(e) => { e.currentTarget.style.maxWidth = e.currentTarget.style.maxWidth ? "" : "220%"; }}
              className="max-w-full w-full object-contain transition-transform duration-300 cursor-zoom-in"
              style={{ maxWidth: "" }}
            />
          </div>
          <button
            data-testid="photo-zoom-close-btn"
            onClick={() => setZoom(false)}
            aria-label="Close zoom"
            className="fixed top-5 right-5 w-11 h-11 border border-white/20 text-dim hover:text-acid hover:border-acid transition-colors duration-300 flex items-center justify-center"
          >
            <X size={18} />
          </button>
          <p className="fixed bottom-5 inset-x-0 text-center font-mono text-[10px] tracking-[0.25em] text-mute">CLICK PHOTO TO ZOOM IN / OUT — ESC TO CLOSE</p>
        </div>
      )}
    </main>
  );
}
